// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import useCreateInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useCreateInstrumentConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistryInstrumentConfigurations from '../../../hooks/queries/registry/configuration/useRegistryInstrumentConfigurations';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import {
  toInstrumentIdentifiers,
  InstrumentIdentifierElement,
} from '../../../modules/Instrument/Common';
import InstrumentIdentifiersInput from '../../../modules/Instrument/InstrumentIdentifiersInput';
import { ERR_CRE_INST_CONF, SUC_CRE_INST_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

const CreateInstrumentConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>('');
  const [additionalIdentifiers, setAdditionalIdentifiers] = useState<InstrumentIdentifierElement[]>(
    [],
  );
  const [instrumentIssuerCredentialRequirements, setInstrumentIssuerCredentialRequirements] =
    useState<PartyCredentialRequirementElement[]>([]);
  const [instrumentHolderCredentialRequirements, setInstrumentHolderCredentialRequirements] =
    useState<PartyCredentialRequirementElement[]>([]);

  const registrarService = useRegistrarService();
  const mutate = useMutate();
  const createInstrumentConfiguration = useCreateInstrumentConfiguration();
  const instrumentConfigurations = useRegistryInstrumentConfigurations();

  // Check if the instrumentId already exists
  const instrumentIdExists = instrumentConfigurations.data?.some(
    (instrumentConfiguration) =>
      instrumentConfiguration.payload.defaultIdentifier.id === instrumentId,
  );

  const resetState = useCallback(() => {
    setInstrumentId('');
    setAdditionalIdentifiers([]);
    setInstrumentIssuerCredentialRequirements([]);
    setInstrumentHolderCredentialRequirements([]);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!registrarService.data) return;
    const payload = {
      registrarServiceCid: registrarService.data.contractId,
      instrumentId,
      additionalIdentifiers: toInstrumentIdentifiers(additionalIdentifiers),
      issuerRequirements: toPartyCredentialRequirements(instrumentIssuerCredentialRequirements),
      holderRequirements: toPartyCredentialRequirements(instrumentHolderCredentialRequirements),
    };
    await mutate(createInstrumentConfiguration, payload, SUC_CRE_INST_CONF, ERR_CRE_INST_CONF);
    handleClose();
  }, [
    registrarService.data,
    createInstrumentConfiguration,
    instrumentId,
    additionalIdentifiers,
    instrumentIssuerCredentialRequirements,
    instrumentHolderCredentialRequirements,
    handleClose,
    mutate,
  ]);

  const { isLoading } = registrarService;
  if (isLoading) return null;

  const disableSubmit =
    !instrumentId ||
    instrumentIdExists ||
    additionalIdentifiers.some(({ element: { source, id } }) => !source || !id) ||
    instrumentIssuerCredentialRequirements.some(
      ({ issuer, requiredClaims }) =>
        !issuer ||
        (issuer &&
          (!requiredClaims.length ||
            requiredClaims.some(({ property, value }) => !property || !value))),
    ) ||
    instrumentHolderCredentialRequirements.some(
      ({ issuer, requiredClaims }) =>
        !issuer ||
        (issuer &&
          (!requiredClaims.length ||
            requiredClaims.some(({ property, value }) => !property || !value))),
    );

  return (
    <FormDialog
      open={open}
      title="Create Instrument Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <TextInput
          label="Instrument Id"
          value={instrumentId}
          setValue={(value) => {
            setInstrumentId(value);
          }}
          required
          validationMessage={instrumentIdExists ? 'Instrument ID already exists' : ''}
        />
      </Box>
      <Box mb={3}>
        <InstrumentIdentifiersInput
          instrumentIdentifiers={additionalIdentifiers}
          setInstrumentIdentifiers={setAdditionalIdentifiers}
          title="Additional Instrument Identifiers"
        />
      </Box>
      <Box mb={3}>
        <RequiredCredentialsInput
          list={instrumentIssuerCredentialRequirements}
          setList={setInstrumentIssuerCredentialRequirements}
          title="Required Credentials for Mint Requestor"
        />
      </Box>
      <Box mb={3}>
        <RequiredCredentialsInput
          list={instrumentHolderCredentialRequirements}
          setList={setInstrumentHolderCredentialRequirements}
          title="Required Credentials for Holders"
        />
      </Box>
    </FormDialog>
  );
};

export default CreateInstrumentConfigurationDialog;
